import {
  action,
  computed,
  IObservableArray,
  makeObservable,
  observable,
  runInAction,
  toJS,
} from "mobx";
import { GraphQLError } from "graphql";
import { IStoreElement } from "types/store";
import { ExtendedNextPageContext } from "types/app";

export type FavoriteProductID = Entity.Product["id"];

export interface IFavorites extends IStoreElement {
  list: IObservableArray<FavoriteProductID>;
  total: number | null;
}

export default class Favorites implements IFavorites {
  private ctx?: ExtendedNextPageContext;
  @observable list = [] as IObservableArray<FavoriteProductID>;
  @observable total = null;

  constructor() {
    makeObservable(this);
  }

  toJSON() {
    return toJS(this);
  }

  @computed
  get count(): number {
    return this.total;
  }

  @action
  hydrate(data: Partial<IFavorites>): void {
    if (data.items && Array.isArray(data.items)) {
      this.list = data.list as IObservableArray<FavoriteProductID>;
      this.total = data.total;
    }
  }

  @action
  updateList(list: Array<Entity.ID>, total: number): void {
    this.list = [...toJS(list)] as IObservableArray<FavoriteProductID>;
    this.total = total;
  }

  @action
  async update(
    productID: Entity.ID,
    mode: Api.Favorites.UpdateFavoriteMode = "update"
  ): Promise<{
    data?: Partial<IFavorites>;
    errors?: ReadonlyArray<GraphQLError>;
  }> {
    const { data, errors } = await this.ctx.api.methods.favorites.add({
      mode: mode,
      productID: productID,
      regionID: this.ctx.store.location.regionID,
    });

    if (data) {
      runInAction(() => {
        this.list = data.updateFavoriteProducts
          .items as IObservableArray<Entity.ID>;
        this.total = data.updateFavoriteProducts.total;
      });
    }

    return { data, errors };
  }

  @action
  async add(productID: FavoriteProductID): Promise<void> {
    await this.update(productID);
  }

  @action
  async remove(productID: FavoriteProductID): Promise<void> {
    await this.update(productID, "delete");
  }

  @action
  reset(): void {
    this.list = [] as IObservableArray<FavoriteProductID>;
    this.total = 0;
  }

  has(productId: FavoriteProductID): boolean {
    return this.list && this.list.indexOf(productId) !== -1;
  }
}
